import {chargeableKg, expandSelection, round2, volumetricKg} from './catalogueMath'

/** Provenance vocabulary. String enum — never an index, and never compared against the label. */
export const SKU_SOURCE = {CATALOGUE: 'catalogue', AUTO: 'auto', OMS: 'oms'}

export const SOURCE_LABEL = {
  catalogue: 'Catalogue upload',
  auto: 'Auto-added from shipment',
  oms: 'OMS-WMS sync',
}

/** The whole mutable surface of this section: the SKU list plus an in-session merge log. */
export function initialState(skus) {
  return {skus: (skus || []).slice(), mapLog: []}
}

export function filterSkus(skus, {q, source, sub}) {
  const needle = String(q || '').trim().toLowerCase()
  return (skus || []).filter((s) => {
    if (source && source !== 'all' && s.source !== source) return false
    if (sub && sub !== 'all' && s.sub !== sub) return false
    if (needle) {
      const hay = (s.sku + ' ' + s.cat + ' ' + s.sub + ' ' + (s.product || '')).toLowerCase()
      if (hay.indexOf(needle) < 0) return false
    }
    return true
  })
}

/**
 * Row identity for pagination. Deliberately EXCLUDES every weight: editing a
 * dead weight, a packing material's volumetric or a chargeable weight must
 * never throw the table back to page one under the merchant's cursor. Only a
 * change to the row SET, its labels, its provenance or its mapping counts.
 */
export function rowSignature(skus) {
  return (skus || [])
    .map((s) => [
      s.sku, s.cat, s.sub, s.source,
      s.childOf || '', (s.motherOf || []).join('+'),
      s.reviewed ? 1 : 0, s.dq ? 1 : 0,
    ].join('|'))
    .join('~')
}

/** Selection is held independently of the filters; only a code that stopped existing is dropped. */
export function selectionCodes(selection, skus) {
  const live = new Set((skus || []).map((s) => s.sku))
  return Array.from(selection || []).filter((c) => live.has(c))
}

/** Codes in the selection that already report to a mother — they cannot be re-parented. */
export function mergeBlockers(selection, skus) {
  const byCode = new Map((skus || []).map((s) => [s.sku, s]))
  return selectionCodes(selection, skus).filter((c) => !!byCode.get(c).childOf)
}

export function canMerge(selection, skus) {
  return selectionCodes(selection, skus).length >= 2 && mergeBlockers(selection, skus).length === 0
}

/**
 * Merge. `codes` is whatever the merchant ticked; expandSelection pulls any
 * existing children along so the result is ONE flat mother with every code
 * under it — a child never keeps its own motherOf.
 *
 * `at` and `by` are passed in, never read from a clock or a global: the app
 * clock is pinned (NOW) and the actor comes from the merchant record.
 */
export function applyMerge(state, {mother, codes, at, by}) {
  const children = expandSelection(state.skus, codes).filter((c) => c !== mother)

  const skus = state.skus.map((s) => {
    if (s.sku === mother) {
      const have = (s.motherOf || []).slice()
      children.forEach((k) => {
        if (have.indexOf(k) < 0) have.push(k)
      })
      return {...s, motherOf: have, childOf: null}
    }
    if (children.indexOf(s.sku) >= 0) return {...s, childOf: mother, motherOf: null}
    return s
  })

  const entry = {id: 'MM-' + at + '-' + mother, at, by, mother, children: children.slice(), undone: null, undoneBy: null}
  return {skus, mapLog: [entry].concat(state.mapLog)}
}

/**
 * Unmerge is a SOFT reversal. The log row is stamped `undone`/`undoneBy` and
 * kept forever — the merchant needs to see that a merge happened and was
 * backed out, not an absence. Never splice the entry out.
 */
export function applyUndoMerge(state, {entryId, at, by}) {
  const entry = state.mapLog.find((e) => e.id === entryId)
  if (!entry || entry.undone) return state

  const skus = state.skus.map((s) => {
    if (s.sku === entry.mother) {
      return {...s, motherOf: (s.motherOf || []).filter((c) => entry.children.indexOf(c) < 0)}
    }
    if (entry.children.indexOf(s.sku) >= 0 && s.childOf === entry.mother) return {...s, childOf: null}
    return s
  })

  const mapLog = state.mapLog.map((e) => (e.id === entryId ? {...e, undone: at, undoneBy: by} : e))
  return {skus, mapLog}
}

/** A code prefix the brand would recognise, taken from the longest common head of its own codes. */
export function codePrefix(skus, merchantName) {
  const codes = (skus || []).slice(0, 8).map((s) => s.sku)
  let p = codes[0] || ''
  codes.forEach((c) => {
    let i = 0
    while (i < p.length && i < c.length && p[i] === c[i]) i += 1
    p = p.slice(0, i)
  })
  const cut = Math.max(p.lastIndexOf('-'), p.lastIndexOf('_'))
  p = cut > 0 ? p.slice(0, cut) : ''
  if (p.replace(/[^A-Za-z0-9]/g, '').length < 2) {
    p = (String(merchantName || '').replace(/[^A-Za-z]/g, '').slice(0, 2) || 'BR').toUpperCase()
  }
  return p
}

export function abbr(text) {
  const t = String(text || 'GEN').replace(/[^A-Za-z0-9 ]/g, ' ').trim().split(/\s+/)[0] || 'GEN'
  return t.slice(0, 3).toUpperCase()
}

export function uniqueCode(taken, base) {
  let candidate = base
  let n = 2
  while (taken.indexOf(candidate) >= 0) {
    candidate = base + '-' + n
    n += 1
  }
  return candidate
}

/** Median dead weight of the SKUs already in a sub-category, so an imported row starts from something the brand actually ships. */
export function medianDead(skus, sub) {
  const v = (skus || []).filter((s) => s.sub === sub && s.deadKg).map((s) => s.deadKg).sort((a, b) => a - b)
  if (!v.length) return 0.5
  return round2(v[Math.floor(v.length / 2)])
}

/** Distinct category / sub-category pairs drawn from the merchant's own catalogue. */
export function catSubPairs(skus, n) {
  const seen = new Set()
  const out = []
  ;(skus || []).forEach((s) => {
    const k = s.cat + '|' + s.sub
    if (seen.has(k)) return
    seen.add(k)
    out.push({cat: s.cat, sub: s.sub})
  })
  while (out.length < n) out.push(out[0] || {cat: 'General', sub: 'Uncategorised'})
  return out.slice(0, n)
}

/**
 * A blank SKU record carrying every field the rest of the module reads —
 * including the two evidence counts, which start at zero because a SKU that
 * has never shipped has no sorter reading and no merchant evidence.
 * `reviewed` is true for anything the merchant deliberately imported and
 * false for an auto-added row, which is precisely the review backlog.
 */
export function newSku({sku, cat, sub, dead, packId, source, packs}) {
  const pack = (packs || []).find((p) => p.id === packId) || (packs || [])[0] || null
  const deadKg = round2(dead || 0.5)
  const volKg = pack && pack.volKg != null ? pack.volKg : (pack && pack.dims ? volumetricKg(pack.dims) : round2(deadKg * 0.8))
  return {
    sku,
    product: sku,
    ships: 0,
    hist: [],
    mpw: 0,
    spread: 0,
    dq: false,
    cat,
    sub,
    deadKg,
    volKg,
    chargeKg: chargeableKg(deadKg, volKg),
    packId: pack ? pack.id : null,
    sorterCount: 0,
    merchantCount: 0,
    source,
    reviewed: source !== SKU_SOURCE.AUTO,
    motherOf: null,
    childOf: null,
  }
}

/**
 * The rows a catalogue file would carry, drawn deterministically from the
 * merchant's own records: three existing codes nudged, three new ones minted.
 *
 * NOTHING IS PARSED. The mockup never read a file either — it fabricated this
 * preview — and this port keeps that honest boundary rather than pretending to
 * a CSV/XLSX importer that does not exist. The upload drawer says so on screen.
 */
export function plannedImportRows(skus, packs) {
  const list = Array.isArray(skus) ? skus : []
  // A merchant can legitimately have no packing materials recorded, and the catalogue load can fail
  // and leave this empty. Either way the arithmetic below divides by packs.length, so guard once
  // here: with no packs there is nothing to rotate through and no preview to draw.
  const boxes = Array.isArray(packs) ? packs : []
  if (!boxes.length) return {updated: [], added: []}

  const updated = [6, 13, 21]
    .map((i) => list[i])
    .filter(Boolean)
    .map((s, n) => {
      const at = boxes.findIndex((p) => p.id === s.packId)
      const next = boxes[(at + 1 + boxes.length) % boxes.length]
      return {
        sku: s.sku,
        cat: s.cat,
        sub: s.sub,
        fromDead: s.deadKg,
        dead: round2(Math.max(0.05, s.deadKg + (n === 1 ? -0.05 : 0.05))),
        fromPack: s.packId,
        packId: next.id,
      }
    })

  const prefix = codePrefix(list)
  const taken = list.map((s) => s.sku)
  const added = catSubPairs(list, 3).map((cs, i) => {
    const code = uniqueCode(taken, prefix + '-' + abbr(cs.sub) + '-' + (91 + i))
    taken.push(code)
    const like = list.find((x) => x.sub === cs.sub)
    return {sku: code, cat: cs.cat, sub: cs.sub, dead: medianDead(list, cs.sub), packId: like ? like.packId : boxes[0].id}
  })

  return {updated, added}
}

export function applyImport(state, {rows, packs}) {
  const skus = state.skus.slice()

  rows.updated.forEach((r) => {
    const i = skus.findIndex((s) => s.sku === r.sku)
    if (i < 0) return
    const pack = (packs || []).find((p) => p.id === r.packId)
    const volKg = pack && pack.volKg != null ? pack.volKg : skus[i].volKg
    skus[i] = {
      ...skus[i],
      deadKg: r.dead,
      packId: r.packId,
      volKg,
      chargeKg: chargeableKg(r.dead, volKg),
      source: SKU_SOURCE.CATALOGUE,
      reviewed: true,
    }
  })

  rows.added.forEach((r) => {
    if (skus.some((s) => s.sku === r.sku)) return
    skus.push(newSku({...r, dead: r.dead, source: SKU_SOURCE.CATALOGUE, packs}))
  })

  return {...state, skus}
}

export function applyOmsPull(state, {merchantName, packs}) {
  const skus = state.skus.slice()
  const taken = skus.map((s) => s.sku)
  const prefix = codePrefix(state.skus, merchantName)

  catSubPairs(state.skus, 3).forEach((cs, i) => {
    const code = uniqueCode(taken, prefix + '-WMS-' + abbr(cs.sub) + '-' + (1 + i))
    taken.push(code)
    const like = state.skus.find((s) => s.sub === cs.sub)
    skus.push(newSku({
      sku: code,
      cat: cs.cat,
      sub: cs.sub,
      dead: medianDead(state.skus, cs.sub),
      packId: like ? like.packId : packs[0].id,
      source: SKU_SOURCE.OMS,
      packs,
    }))
  })

  return {...state, skus}
}

export function applyReview(state, {code, patch, packs}) {
  const pack = (packs || []).find((p) => p.id === patch.packId)
  const skus = state.skus.map((s) => {
    if (s.sku !== code) return s
    const volKg = pack && pack.volKg != null ? pack.volKg : s.volKg
    return {
      ...s,
      cat: patch.cat,
      sub: patch.sub,
      packId: patch.packId,
      volKg,
      chargeKg: chargeableKg(s.deadKg, volKg),
      reviewed: true,
    }
  })
  return {...state, skus}
}

/** Bulk confirm. A SKU whose code is unusable is skipped — its code has to be fixed first. */
export function applyMarkReviewed(state, {codes}) {
  const set = new Set(codes || [])
  const skus = state.skus.map((s) => (set.has(s.sku) && !s.dq ? {...s, reviewed: true} : s))
  return {...state, skus}
}

/**
 * Give an unusable product code a real one. The code is the row key everywhere
 * in this module, so every reference to the old value has to move with it:
 * a child's `childOf`, and a mother's `motherOf` entry.
 * `reviewed` resets to false — a renamed row has not been checked under its new code.
 */
export function applyFixCode(state, {code, nextCode}) {
  const skus = state.skus.map((s) => {
    if (s.sku === code) return {...s, sku: nextCode, dq: false, reviewed: false}
    if (s.childOf === code) return {...s, childOf: nextCode}
    if ((s.motherOf || []).indexOf(code) >= 0) {
      return {...s, motherOf: s.motherOf.map((c) => (c === code ? nextCode : c))}
    }
    return s
  })
  return {...state, skus}
}
